import {useId, useState, type FormEvent} from 'react';
import {userMessageDisplayText} from './lib/conversation-messages';

type Question = {id: string; question: string; choices: string[]; multi: boolean};
type Draft = {text: string; selected: string[]; skipped: boolean};
type Props = {
  request: Record<string, unknown>;
  busy: boolean;
  messagePrefix: string;
  answer: (requestId: string, result: Record<string, unknown>) => Promise<unknown>;
};

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

export function parseNativeQuestion(request: Record<string, unknown>) {
  const params = record(request.params);
  if (request.method !== 'clarify' || typeof request.id !== 'string' || !params) {
    throw new Error('This Hermes request does not have a supported response form.');
  }
  const multiple = Object.hasOwn(params, 'questions');
  if (multiple && !Array.isArray(params.questions)) throw new Error('Hermes questions must be an array.');
  const values = multiple ? params.questions as unknown[] : [params];
  if (values.length === 0) throw new Error('Hermes supplied no questions.');
  const questions: Question[] = values.map((entry, index) => {
    const value = record(entry);
    if (!value || typeof value.question !== 'string' || (multiple && typeof value.qid !== 'string')) {
      throw new Error('A Hermes question is missing its question text or request ID.');
    }
    const choices = value.choices ?? [];
    if (!Array.isArray(choices) || !choices.every((choice): choice is string => typeof choice === 'string')) {
      throw new Error('Hermes supplied an unsupported question choice.');
    }
    return {id: multiple ? String(value.qid) : String(index), question: value.question,
      choices, multi: value.multi_select === true && choices.length > 0};
  });
  if (new Set(questions.map(question => question.id)).size !== questions.length) {
    throw new Error('Hermes supplied repeated question IDs.');
  }
  const locked = params.answers === undefined ? {} : record(params.answers);
  if (!locked || Object.entries(locked).some(([id, value]) =>
    !questions.some(question => question.id === id) || typeof value !== 'string')) {
    throw new Error('Hermes supplied unsupported saved answers.');
  }
  return {id: request.id, multiple, questions, locked: locked as Record<string, string>};
}

function serialize(question: Question, draft: Draft | undefined): string | undefined {
  if (!draft) return undefined;
  if (draft.skipped) return '';
  if (!question.multi) return draft.text.trim() ? draft.text : undefined;
  const answers = [...draft.selected, ...(draft.text.trim() ? [draft.text] : [])];
  // The native callback returns text; clarify_tool decodes JSON lists for multi-select.
  return answers.length ? JSON.stringify(answers) : undefined;
}

function SavedAnswer({question, value, messagePrefix}: {question: Question; value: string; messagePrefix: string}) {
  if (value === '') return <p className="field-help">Skipped.</p>;
  if (question.multi && value.trim().startsWith('[')) {
    // Display the native encoded answer without copying it into the editable draft.
    try {
      const items: unknown = JSON.parse(value);
      if (Array.isArray(items) && items.every(item => typeof item === 'string')) {
        return <ul>{items.map((item, index) => <li key={index}>{userMessageDisplayText(item, messagePrefix)}</li>)}</ul>;
      }
    } catch { /* Preserve the exact native answer when it is not an encoded list. */ }
  }
  return <pre>{userMessageDisplayText(value, messagePrefix)}</pre>;
}

export function NativeQuestion({request, answer, busy, messagePrefix}: Props) {
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const formId = useId();
  let parsed;
  try {parsed = parseNativeQuestion(request);}
  catch (error) {
    return <div className="error-notice" role="alert"><p>{error instanceof Error ? error.message : String(error)}</p>
      {error instanceof Error && <pre>{error.stack}</pre>}<pre>{JSON.stringify(request, null, 2)}</pre></div>;
  }
  const {id, multiple, questions, locked} = parsed;
  const editable = questions.filter(question => !Object.hasOwn(locked, question.id));
  const complete = editable.every(question => serialize(question, drafts[question.id]) !== undefined);
  function update(questionId: string, changes: Partial<Draft>): void {
    setDrafts(previous => ({...previous, [questionId]: {
      text: '', selected: [], skipped: false, ...previous[questionId], ...changes,
    }}));
  }
  async function send(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (busy || !complete) return;
    const answers = Object.fromEntries(editable.map(question => [question.id, serialize(question, drafts[question.id])]));
    const result = multiple ? {answers} : {answer: Object.hasOwn(locked, '0') ? locked['0'] : answers['0']};
    try {await answer(id, result);}
    catch { /* The shared operation notice displays the request error. Keep the draft. */ }
  }
  return <form className="native-question" onSubmit={event => void send(event)}>
    {questions.map((question, questionIndex) => {
      const saved = Object.hasOwn(locked, question.id);
      const draft = drafts[question.id];
      const skipped = draft?.skipped === true;
      const textId = `${formId}-${questionIndex}`;
      return <fieldset key={question.id} disabled={busy || saved}>
        <legend>{question.question}</legend>
        {saved ? <><p className="field-help">Your answer is saved.</p>
          <SavedAnswer question={question} value={locked[question.id]!} messagePrefix={messagePrefix}/></> : <>
          {question.choices.length > 0 && <div className="native-question-choices">
            <p className="field-help">{question.multi ? 'Select all answers that apply. You can also add an answer.'
              : 'Select an answer or enter your own.'}</p>
            {question.choices.map((choice, index) => <label className="native-question-choice" key={index}>
              <input type={question.multi ? 'checkbox' : 'radio'} name={`${textId}-choices`} disabled={skipped}
                checked={question.multi ? draft?.selected.includes(choice) === true : draft?.text === choice}
                onChange={event => {
                  if (!question.multi) {update(question.id, {text: choice}); return;}
                  const checked = event.target.checked;
                  setDrafts(previous => {
                    const current = previous[question.id] ?? {text: '', selected: [], skipped: false};
                    return {...previous, [question.id]: {...current, selected: checked
                      ? [...new Set([...current.selected, choice])] : current.selected.filter(value => value !== choice)}};
                  });
                }}/><span>{choice}</span></label>)}
          </div>}
          <label className="field" htmlFor={textId}><span>{question.multi ? 'Additional answer' : 'Your answer'}</span>
            <textarea id={textId} rows={3} disabled={skipped} value={draft?.text ?? ''}
              onChange={event => update(question.id, {text: event.target.value})}/></label>
          <label className="native-question-choice"><input type="checkbox" checked={skipped}
            onChange={event => update(question.id, {skipped: event.target.checked})}/><span>Skip this question</span></label>
        </>}
      </fieldset>;
    })}<button type="submit" className="button" disabled={busy || !complete}>Send answers</button>
  </form>;
}
